import { CounterInput } from '@mm/components/src/CounterInput/CounterInput';
import classNames from 'classnames';
import moment, { unitOfTime } from 'moment';
import pluralize from 'pluralize';
import React, { useMemo } from 'react';

import PrimarySelect, {
  PrimarySelectOuterProps,
} from '../../../components/PrimarySelect/PrimarySelect';
import './TimePeriodselector.pcss';

type OnChange = PrimarySelectOuterProps['onChange'];

type OnBlur = Pick<React.ComponentProps<typeof CounterInput>, 'onBlur'>;

export type Measure = unitOfTime.Base;

export interface ITimePeriod extends OnBlur {
  className: string;
  value: number | null;
  measure: Measure | null;
  max?: number;
  onError: () => void;
  onValueChange: (cappedValue: number) => void;
  onMeasureChange: OnChange;
}

const getFormattedOptions = (value?: number | string | null) => {
  return [
    {
      id: 'minute',
      name: pluralize('Minute', parseInt(value?.toString() || '', 10)),
    },
    {
      id: 'hour',
      name: pluralize('Hour', parseInt(value?.toString() || '', 10)),
    },
    {
      id: 'day',
      name: pluralize('Days', parseInt(value?.toString() || '', 10)),
    },
  ];
};

export const formatTimePeriod = (
  value?: string | number | null,
  measure?: Measure | null,
) => {
  return `${value} ${
    getFormattedOptions(value).find(opt => opt.id === measure)?.name || ''
  }`;
};

const getMaxDefault = measure => moment.duration(23, 'hour').as(measure);

const getCounterValue = (value?: number | null) => {
  return value || 0;
};

export const TimePeriodSelector = ({
  className,
  measure,
  value: propValue,
  max: maxValue,
  onBlur,
  onError = () => {},
  onValueChange = () => {},
  onMeasureChange = () => {},
}: Partial<ITimePeriod>) => {
  const value = useMemo(() => getCounterValue(propValue), [propValue]);
  const max = useMemo(() => maxValue ?? getMaxDefault(measure), [measure]);

  const options = useMemo(() => getFormattedOptions(value), [value]);

  const onInputValueChange = (value: string) => {
    let num = parseInt(value, 10);
    num = num > max ? max : num;
    num = num < 1 ? 1 : num;
    if (num > max) {
      onError?.();
    } else {
      onValueChange(num);
    }
  };

  const measureValue = options.find(
    opt => opt.id.toLowerCase() === measure?.toLowerCase(),
  );

  return (
    <div className={classNames(className, 'time-period-selector__container')}>
      <CounterInput
        css={{
          container: 'time-period-selector__counter',
          input: 'time-period-selector__counter-input',
        }}
        min={1}
        max={max}
        value={value}
        onBlur={onBlur}
        onChange={onValueChange}
        onInputChange={onInputValueChange}
      />

      <PrimarySelect
        className="time-period-selector__selector"
        activeItemClassName="time-period-selector__selector-item"
        value={measureValue}
        options={options}
        onChange={onMeasureChange}
      />
    </div>
  );
};
